<template>
  <VDialog :value="dialog.open" transition="dialog-bottom-transition" fullscreen hide-overlay persistent>
    <StepSelectPlan v-if="step === 1" @next="handleSelectPlan" @close="handleClose" />
    <StepPaymentMethod v-if="step === 2" @selected="handleSelectPaymentMethod" @back="handleBack" />

    <template v-if="step === 3 && paymentMethod">
      <CreditCardPayment v-if="paymentMethod === 'CREDIT_CARD'" @save="handleSaveCreditCard" @back="handleBack" />
      <BoletoPayment v-if="paymentMethod === 'BOLETO'" @save="handleSave" @back="handleBack" @finish="goToInvoices" />
      <PixPayment v-if="paymentMethod === 'PIX'" @save="handleSave" @back="handleBack" @finish="goToInvoices" />
    </template>

    <CurrentSignature v-if="step === 4" @change-plan="handleChangePlan" @close="handleClose" />
  </VDialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import PlanSignatureService from '@/services/api/planSignature';
import OrganizationSignatureService from '@/services/api/organizationSignature';

import StepSelectPlan from './Steps/StepSelectPlan';
import StepPaymentMethod from './Steps/StepPaymentMethod';
import CreditCardPayment from './PaymentMethods/CreditCardPayment';
import BoletoPayment from './PaymentMethods/BoletoPayment';
import PixPayment from './PaymentMethods/PixPayment';
import CurrentSignature from './CurrentSignature/CurrentSignature';

export default {
  props: {
    dialog: Object
  },
  components: { StepSelectPlan, StepPaymentMethod, CreditCardPayment, BoletoPayment, PixPayment, CurrentSignature },
  data() {
    return {
      step: 1
    };
  },
  mounted() {
    this.getPlans().then(() => {
      this.step = this.dialog?.step || 1;
      if (this.step === 1) this.notifyPlansCheckout();
    });
  },
  computed: {
    ...mapState('Payment', ['selectedPlan', 'cycle', 'paymentMethod', 'coupon', 'creditCardInfo', 'totalPrice'])
  },
  methods: {
    ...mapActions('Payment', [
      'setPlans',
      'setSelectedPlan',
      'setLoading',
      'setCoupon',
      'setPaymentMethod',
      'setPaymentCode',
      'setCreditCardInfo',
      'resetState'
    ]),
    async handleSelectPaymentMethod(method) {
      this.setPaymentMethod(method);

      if (method === 'FREE') {
        await this.handleSave();
        return;
      }

      this.step = 3;
    },
    notifyPlansCheckout() {
      let rastreio = {};
      try {
        if (window.CheckMotorsRastreio && typeof window.CheckMotorsRastreio.coletar === 'function') {
          rastreio = window.CheckMotorsRastreio.coletar();
        }
      } catch (error) {
        rastreio = {};
      }

      const body = { rastreio };
      const planId = Number(this.selectedPlan?.id);
      if (Number.isInteger(planId) && planId > 0) {
        body.plano_id = planId;
      }

      this.$http.post('/rastreio/planos', body).catch(() => {});
    },
    async getPlans() {
      try {
        this.setLoading(true);
        const { data } = await PlanSignatureService.search();
        const customPlan = {
          id: 0,
          code: 'custom',
          title: 'Personalizado',
          description: 'Para oficinas que precisam de um plano personalizado.',
          price: null,
          annual_discount: null
        };

        const filteredData = data.filter((d) => !d.free_for_days);
        const freePlans = data.filter((d) => d.free_for_days);

        this.setPlans([...filteredData, customPlan, ...freePlans]);
        this.setSelectedPlan(data[0]);
      } finally {
        this.setLoading(false);
      }
    },
    async handleSelectPlan() {
      if (this.selectedPlan?.free_for_days) {
        this.setPaymentMethod('FREE');
        await this.handleSave();
        return;
      }

      this.step = 2;
    },
    async handleChangePlan() {
      if (this.selectedPlan) {
        this.resetState();
        await this.getPlans();
      }

      this.step = 1;
      this.notifyPlansCheckout();
    },
    handleBack() {
      const currentStep = this.step;
      this.setPaymentCode(null);
      if (currentStep === 1) return;

      if (currentStep === 2) {
        this.setCoupon(null);
      }

      if (currentStep === 3) {
        this.setPaymentMethod(null);
        this.setCreditCardInfo(null);
      }

      this.step = currentStep - 1;
    },
    handleSaveCreditCard(creditCardInfo) {
      this.setCreditCardInfo(creditCardInfo);

      this.handleSave();
    },
    goToInvoices() {
      this.step = 4;
    },
    async handleSave() {
      try {
        this.setLoading(true);
        const planSignature = {
          cycle: this.cycle,
          plan: this.selectedPlan,
          paymentMethod: this.paymentMethod,
          coupon: this.coupon,
          creditCardInfo: this.creditCardInfo,
          totalPrice: this.totalPrice
        };

        const { data } = await OrganizationSignatureService.create(planSignature);

        if (['PIX', 'BOLETO'].includes(data.paymentMethod)) {
          this.setPaymentCode(data.code);
        }

        if (this.paymentMethod === 'FREE') {
          this.handleClose();
          return;
        }

        this.goToInvoices();
      } finally {
        this.setLoading(false);
      }
    },
    handleClose() {
      this.$emit('close');
      this.step = 1;
      this.resetState();
    }
  }
};
</script>
<style lang="scss">
.plan-cycle {
  display: flex;
  border-radius: 8px;
  background-color: #fff;
  padding: 6px;
  gap: 8px;

  button {
    flex-grow: 1;
    font-size: 15px;
    font-weight: 600;
    padding: 6px;
    border-radius: 4px;

    &.selected {
      color: #fff;
      background-color: #48b48d;
    }

    span {
      color: #e8663d;
    }
  }
}
</style>
